/**
 * Which hire lengths the checkout accepts, and what it says about the rest
 * (spec §11 · I4).
 *
 * > Ceny: odgórne, stałe, progi: 1 dzień, 3 dni, 7 dni, 14 dni, 30 dni.
 * > Inne długości niedozwolone.
 *
 * This replaces a percentage ladder, and the difference is not cosmetic. Until
 * I4 a hire was a DAILY RATE with a `RENTAL_DAYS` rung applied to whatever
 * number of days the window happened to span — 1 to 6 days of rungs against a
 * `MAX_RENTAL_DAYS` of 365, so a five-day hire and a two-hundred-day hire both
 * had a price and neither was one spec §11 allows. A threshold is a PRICE, not
 * a discount off something else, and that is what the catalogue now stores.
 *
 * The table itself lives in `@repo/db/rental-terms`, beside the seed that
 * writes the variants — see the note there. What is here is the part the
 * checkout needs: the refusal, and the words it carries.
 */
export {
  dailyFromTermPrice,
  isRentalTerm,
  RENTAL_TERM_DISCOUNT_BPS,
  RENTAL_TERMS,
  type RentalTerm,
  termLabel,
  termOfSlug,
  termPriceMinor,
  termSlug,
} from '@repo/db/rental-terms'

import type { RentalTerm } from '@repo/db/rental-terms'
import { isRentalTerm, RENTAL_TERMS } from '@repo/db/rental-terms'

/** The thresholds written out, for a refusal somebody can act on. */
export const RENTAL_TERMS_TEXT = RENTAL_TERMS.map((days) =>
  days === 1 ? '1 dzień' : `${String(days)} dni`,
).join(', ')

/**
 * Why this length cannot be hired — or `null` when it can.
 *
 * The message NAMES the thresholds, because "inne długości niedozwolone" is
 * useless to somebody who picked five days: the only useful answer is which
 * lengths exist. Spec §11 gives five and there is no arithmetic that turns a
 * refused length into an allowed one, so the refusal has to carry the list.
 */
export function rentalTermProblem(days: number): string | null {
  if (!Number.isInteger(days) || days < 1) return 'Wybierz termin odbioru i zwrotu.'
  if (isRentalTerm(days)) return null
  return `Sprzęt wynajmujemy na ${RENTAL_TERMS_TEXT}. Wybrany termin to ${String(days)} dni — wybierz jeden z progów.`
}

/** The shortest threshold that covers this many days; the longest if none does. */
export function coveringTerm(days: number): RentalTerm {
  return RENTAL_TERMS.find((term) => term >= days) ?? 30
}

/** The next threshold up, or `null` at the top. */
export function nextTerm(days: number): RentalTerm | null {
  return RENTAL_TERMS.find((term) => term > days) ?? null
}
